/**
 * Archive / restore property — Settings danger zone (owner only).
 * Styled for Slice 9 Settings chrome.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Archive, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useArchiveProperty, useRestoreProperty } from "@/hooks/useArchivedProperties";

interface DeletePropertySectionProps {
  propertyId: string;
  propertyName: string;
  archivedAt: string | null;
}

const DeletePropertySection = ({ propertyId, propertyName, archivedAt }: DeletePropertySectionProps) => {
  const navigate = useNavigate();
  const archiveProperty = useArchiveProperty();
  const restoreProperty = useRestoreProperty();

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");

  const isConfirmationValid =
    confirmationText.trim().toLowerCase() === propertyName.trim().toLowerCase();
  const isArchived = archivedAt != null;

  const handleArchive = async () => {
    if (!isConfirmationValid) return;
    try {
      await archiveProperty.mutateAsync(propertyId);
      toast({
        title: "Property archived",
        description: `${propertyName} has been moved to your archived properties.`,
      });
      navigate("/owner/properties");
    } catch (error) {
      console.error("Error archiving property:", error);
      toast({
        title: "Couldn’t archive",
        description: "Failed to archive property. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowArchiveModal(false);
      setConfirmationText("");
    }
  };

  const handleRestore = async () => {
    try {
      await restoreProperty.mutateAsync(propertyId);
      toast({
        title: "Property restored",
        description: `${propertyName} is active again.`,
      });
    } catch (error) {
      console.error("Error restoring property:", error);
      toast({
        title: "Couldn’t restore",
        description: "Failed to restore property. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isArchived) {
    return (
      <div className="flex flex-col gap-3 rounded-[18px] border-[1.5px] border-[#f0d9a8] bg-[#fffaf0] px-5 py-5">
        <div className="text-[11px] font-bold tracking-[1px] text-[#8a6a15]">ARCHIVED</div>
        <p className="text-[15px] font-medium text-[#64748b]">
          Cleaners can’t access this property and no new tasks will be created. Restore it to make it
          active again. Permanent delete is on the Archived properties page.
        </p>
        <button
          type="button"
          onClick={handleRestore}
          disabled={restoreProperty.isPending}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-[#24a89a] text-[15px] font-bold text-white hover:bg-[#1f958a] disabled:opacity-60 sm:w-auto sm:px-5"
        >
          {restoreProperty.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Restoring…
            </>
          ) : (
            "Restore property"
          )}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 rounded-[18px] border-[1.5px] border-[#f0d0cb] bg-white px-5 py-5">
        <div className="text-[11px] font-bold tracking-[1px] text-[#b4453a]">DANGER ZONE</div>
        <p className="text-[15px] font-medium text-[#64748b]">
          Archive to disable cleaner access, calendar sync, and task creation. You can restore later,
          or permanently delete from Archived properties.
        </p>
        <button
          type="button"
          onClick={() => setShowArchiveModal(true)}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-[#b4453a] text-[15px] font-bold text-white hover:bg-[#9a3a31] sm:w-auto sm:px-5"
        >
          <Archive className="h-4 w-4" />
          Archive property
        </button>
      </div>

      <AlertDialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <AlertDialogContent className="max-w-md rounded-[20px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#b4453a]">
              <AlertTriangle className="h-5 w-5" />
              Archive this property?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              <p>Archiving will disable:</p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Cleaner access to this property</li>
                <li>Calendar sync and automatic turnovers</li>
                <li>Billing and auto-assign features</li>
              </ul>
              <p>
                You can restore later or permanently delete from the archived properties page.
              </p>
              <p className="font-medium text-[#0f1720]">
                Type <span className="font-bold">"{propertyName}"</span> to confirm:
              </p>
              <Input
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={propertyName}
                className="mt-2"
                autoComplete="off"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel
              onClick={() => setConfirmationText("")}
              className="w-full rounded-[12px] sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              onClick={handleArchive}
              disabled={!isConfirmationValid || archiveProperty.isPending}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-[12px] bg-[#b4453a] px-4 text-sm font-bold text-white disabled:opacity-50 sm:w-auto"
            >
              {archiveProperty.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" />
                  Archive property
                </>
              )}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DeletePropertySection;
